// Content for the "Preuve et résultats / Proof & Results" (Ville de Richelieu) section on the Requests page.
export type Bi = { fr: string; en: string }

export const REFERENCE_IMAGE =
  'https://cdn.magicpatterns.com/uploads/wsEpbSu1wUqqv6Z145HhAG/Image_ChatGPT_27_sept._2026,_18_27_12.png'
export const FIELD_PHOTOS = {
  pole: 'https://cdn.magicpatterns.com/patterns/generated-images/04f96052-6592-4a53-94b1-5404c5a22fd6.jpg',
  lamp: 'https://cdn.magicpatterns.com/patterns/generated-images/c49b31f5-2c74-43aa-a063-a5a04ba80cfc.jpg',
}

export const proofIntro = {
  eyebrow: { fr: 'Preuve et résultats', en: 'Proof & results' },
  title: { fr: 'De la promesse à l’usage réel.', en: 'From promise to real-world use.' },
  subline: {
    fr: 'Déployé de bout en bout à la Ville de Richelieu.',
    en: 'Deployed end-to-end at the City of Richelieu.',
  },
  body: {
    fr: 'De la demande citoyenne à l’affectation des équipes terrain, en passant par le suivi et la résolution, jusqu’à la rétroaction au citoyen, Requêtes ATNA est utilisé comme système opérationnel complet.',
    en: 'From resident request to field assignment, tracking, resolution and resident updates, ATNA Requests is used as a complete operational system.',
  },
}

export const cityCard = {
  status: { fr: 'En production', en: 'Live in production' },
  name: { fr: 'Ville de Richelieu', en: 'City of Richelieu' },
  tagline: {
    fr: 'Une ville connectée, plus efficace et plus proche de ses citoyens.',
    en: 'A connected city, more efficient and closer to its residents.',
  },
  points: [
    { key: 'deployed', text: { fr: 'Module Requêtes déployé de bout en bout', en: 'ATNA Requests deployed end-to-end' } },
    { key: 'field', text: { fr: 'Utilisé par les équipes terrain et les services municipaux', en: 'Used by field teams and municipal services' } },
    { key: 'channels', text: { fr: 'Intégré à l’application, au portail et aux autres canaux', en: 'Integrated with the app, portal and other channels' } },
    { key: 'tracking', text: { fr: 'Suivi complet de la demande à la résolution', en: 'Complete tracking from request to resolution' } },
  ],
  // Editable placeholder — not a verified public quotation.
  quote: {
    fr: '« Requêtes ATNA nous permet de mieux servir nos citoyens et de travailler plus efficacement sur le terrain. »',
    en: '“ATNA Requests helps us better serve our citizens and work more efficiently in the field.”',
  },
  quoteBy: { fr: 'Ville de Richelieu', en: 'City of Richelieu' },
}

export const stages = [
  {
    key: 'citizen',
    title: { fr: '1. Citoyen', en: '1. Resident' },
    line: { fr: 'Soumet une demande', en: 'Submits a request' },
    note: { fr: '(par l’application, le portail, le téléphone ou le courriel)', en: '(via app, portal, phone or email)' },
  },
  {
    key: 'atna',
    title: { fr: '2. ATNA', en: '2. ATNA' },
    line: { fr: 'Comprend · qualifie · priorise', en: 'Understands · qualifies · prioritizes' },
    note: { fr: '(enrichit avec le contexte)', en: '(enriches with context)' },
  },
  {
    key: 'field',
    title: { fr: '3. Équipes terrain', en: '3. Field teams' },
    line: { fr: 'Reçoivent · exécutent · mettent à jour', en: 'Receive · execute · update' },
    note: { fr: '(au moyen de l’application et du tableau de bord)', en: '(via the app and dashboard)' },
  },
  {
    key: 'city',
    title: { fr: '4. Ville + citoyen', en: '4. City + resident' },
    line: { fr: 'Suivi · résolution · visibilité', en: 'Tracking · resolution · visibility' },
    note: { fr: '(notification automatique)', en: '(automatic notification)' },
  },
] as const

export const citizenMock = {
  title: { fr: 'Nouvelle demande', en: 'New request' },
  categories: [
    { key: 'light', label: { fr: 'Éclairage public', en: 'Street lighting' } },
    { key: 'roads', label: { fr: 'Voirie', en: 'Roads' } },
    { key: 'waste', label: { fr: 'Collectes', en: 'Waste collection' } },
    { key: 'parks', label: { fr: 'Parcs et loisirs', en: 'Parks & recreation' } },
    { key: 'permits', label: { fr: 'Permis', en: 'Permits' } },
  ],
  submit: { fr: 'Soumettre', en: 'Submit' },
}

export const atnaMock = {
  title: { fr: 'Analyse de la demande', en: 'Request analysis' },
  checks: [
    { k: { fr: 'Catégorie', en: 'Category' }, v: { fr: 'Éclairage public', en: 'Street lighting' } },
    { k: { fr: 'Priorité', en: 'Priority' }, v: { fr: 'Normale', en: 'Normal' } },
    { k: { fr: 'Doublon', en: 'Duplicate' }, v: { fr: 'Vérifié', en: 'Checked' } },
    { k: { fr: 'Adresse', en: 'Address' }, v: { fr: 'Reconnue', en: 'Recognized' } },
  ],
  address: 'Rue des Patriotes, Richelieu',
  contextTitle: { fr: 'Contexte enrichi', en: 'Enriched context' },
  context: [
    { k: { fr: 'Secteur', en: 'Sector' }, v: { fr: 'A', en: 'A' }, small: null },
    { k: { fr: 'Équipement', en: 'Asset' }, v: { fr: 'Lampadaire #R-437', en: 'Streetlight #R-437' }, small: null },
    {
      k: { fr: 'Demandes similaires', en: 'Similar requests' },
      v: { fr: '2', en: '2' },
      small: { fr: '(30 derniers jours)', en: '(last 30 days)' },
    },
  ],
}

export const fieldMock = {
  title: { fr: 'Tâches terrain', en: 'Field tasks' },
  tabs: [
    { label: { fr: 'À faire', en: 'To do' }, count: '3', active: true },
    { label: { fr: 'En cours', en: 'In progress' }, count: '5', active: false },
    { label: { fr: 'Terminées', en: 'Completed' }, count: null, active: false },
  ],
  task: {
    category: { fr: 'Éclairage public', en: 'Street lighting' },
    status: { fr: 'En cours', en: 'In progress' },
    id: '#25473',
    street: 'Rue des Patriotes',
  },
  steps: [
    { label: { fr: 'Intervention planifiée', en: 'Intervention planned' }, done: true },
    { label: { fr: 'Équipe affectée', en: 'Team assigned' }, done: true },
    { label: { fr: 'Travaux en cours', en: 'Work in progress' }, done: true },
    { label: { fr: 'Résolution', en: 'Resolution' }, done: false },
  ],
}

export const trackingMock = {
  title: { fr: 'Suivi de ma demande', en: 'My request status' },
  steps: [
    { label: { fr: 'Demande reçue', en: 'Request received' }, time: { fr: '12 sept. 10 h 24', en: 'Sep 12, 10:24 AM' } },
    { label: { fr: 'En traitement', en: 'In progress' }, time: { fr: '12 sept. 14 h 02', en: 'Sep 12, 2:02 PM' } },
    { label: { fr: 'Intervention en cours', en: 'Intervention underway' }, time: { fr: '15 sept. 9 h 15', en: 'Sep 15, 9:15 AM' } },
    { label: { fr: 'Résolue', en: 'Resolved' }, time: { fr: '15 sept. 14 h 36', en: 'Sep 15, 2:36 PM' } },
  ],
  success: {
    fr: 'Votre demande est résolue. Merci de nous aider à garder notre ville agréable !',
    en: 'Your request has been resolved. Thank you for helping us keep our city a great place to live!',
  },
}

export const proofCards = [
  { key: 'process', title: { fr: 'Un seul processus', en: 'One end-to-end process' }, sub: { fr: 'de la réception à la résolution', en: 'from intake to resolution' } },
  { key: 'field', title: { fr: 'Utilisé sur le terrain', en: 'Used in the field' }, sub: { fr: 'par les équipes opérationnelles', en: 'by operational teams' } },
  { key: 'trace', title: { fr: 'Traçabilité complète', en: 'Complete traceability' }, sub: { fr: 'tâches, statuts, notes, responsables et historique', en: 'tasks, statuses, notes, owners and history' } },
  { key: 'visibility', title: { fr: 'Visibilité centralisée', en: 'Centralized visibility' }, sub: { fr: 'pour suivre volumes, traitement et performance', en: 'to track volumes, processing and performance' } },
] as const

export const results = {
  eyebrow: { fr: 'Des résultats mesurables', en: 'Measurable results' },
  title: { fr: 'Des opérations qui s’améliorent au fil du temps.', en: 'Operations that improve over time.' },
  note: {
    fr: 'Données réelles du déploiement à la Ville de Richelieu.',
    en: 'Actual deployment data from the City of Richelieu.',
  },
  reentry: {
    value: { fr: '40 % → 0 %', en: '40% → 0%' },
    label: { fr: 'de ressaisie manuelle', en: 'manual re-entry' },
    sub: { fr: 'Éliminée avec Requêtes ATNA', en: 'Eliminated with ATNA Requests' },
    note: {
      fr: 'contre environ 40 % dans un processus municipal fragmenté comparable*',
      en: 'vs. ~40% observed in a comparable fragmented municipal process*',
    },
  },
  time: {
    value: { fr: '12 jours', en: '12 days' },
    label: { fr: 'délai moyen de traitement', en: 'average processing time' },
    sub: { fr: 'Moyenne des 3 derniers mois', en: 'Average over the last 3 months' },
  },
  sla: {
    value: { fr: '60 %', en: '60%' },
    label: { fr: 'des demandes respectant le SLA', en: 'of requests meeting SLA' },
    sub: { fr: 'Dernier mois', en: 'Last month' },
    trend: { fr: '+7 points par rapport aux 6 derniers mois', en: '+7 points vs. last 6 months' },
  },
  chart: {
    title: { fr: 'Respect des SLA en progression', en: 'SLA compliance is improving' },
    points: [
      { value: 53, label: { fr: '6 derniers mois', en: 'Last 6 months' } },
      { value: 58, label: { fr: '3 derniers mois', en: 'Last 3 months' } },
      { value: 60, label: { fr: 'Dernier mois', en: 'Last month' } },
    ],
  },
  footnote: {
    fr: '* Le taux de 40 % est une valeur de référence tirée de l’analyse du processus actuel de la Ville de Brossard. Le délai de 12 jours et les taux de 53 %, 58 % et 60 % proviennent du déploiement de Requêtes ATNA à la Ville de Richelieu.',
    en: '* The 40% figure is a reference based on analysis of the City of Brossard’s current process. The 12-day processing time and 53% → 58% → 60% SLA figures are from the ATNA Requests deployment at the City of Richelieu.',
  },
}
